import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { HandCoins, Check, X, Loader2 } from "lucide-react";

const STATUS_COLORS = { pending: "#F59E0B", paid: "#A3E635", declined: "#EF4444" };

export default function CoinRequests({ onChange, embedded = false }) {
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });
  const [form, setForm] = useState({ recipient: "", coins: "", note: "" });
  const [sugs, setSugs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/bullion/coin-requests"); setReqs(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const onRecipientChange = (v) => {
    setForm({ ...form, recipient: v });
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setSugs([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/bullion/recipients?q=${encodeURIComponent(v.trim())}`); setSugs(data); }
      catch { setSugs([]); }
    }, 250);
  };

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bullion/coin-requests", {
        recipient: form.recipient, coins: Number(form.coins), note: form.note || null });
      toast.success(`Request sent to ${data.to_name} 🪙`);
      setForm({ recipient: "", coins: "", note: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (r, action) => {
    setActing(r.id);
    try {
      const { data } = await api.post(`/bullion/coin-requests/${r.id}/${action}`);
      toast.success(action === "pay" ? `Paid ${data.coins} BLC to ${r.from_name} ✅` : "Request declined");
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActing(null); }
  };

  return (
    <div className={embedded ? "" : "bg-pb-surface border border-pb-border"} data-testid="coin-requests">
      {!embedded && (
        <div className="p-5 border-b border-pb-border flex items-center gap-2">
          <HandCoins className="w-5 h-5 text-pb-cyan" />
          <span className="font-display font-bold text-lg uppercase">Request Coins from a Player</span>
        </div>
      )}
      <div className={embedded ? "space-y-4" : "p-5 space-y-4"}>
        {reqs.incoming.length > 0 && (
          <div className="space-y-2" data-testid="coin-requests-incoming">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400">Requests waiting on you</div>
            {reqs.incoming.map((r) => (
              <div key={r.id} data-testid={`coin-request-${r.id}`} className="flex items-center gap-3 border border-amber-500/40 bg-amber-500/5 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate"><b>{r.from_name}</b> requests <b className="text-pb-red">{r.coins} BLC</b></div>
                  {r.note && <div className="text-[11px] text-zinc-500 truncate">“{r.note}”</div>}
                </div>
                <button data-testid={`coin-request-pay-${r.id}`} onClick={() => act(r, "pay")} disabled={acting === r.id}
                  className="flex items-center gap-1 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                  {acting === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Pay
                </button>
                <button data-testid={`coin-request-decline-${r.id}`} onClick={() => act(r, "decline")} disabled={acting === r.id}
                  className="flex items-center gap-1 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
                  <X className="w-3 h-3" /> Decline
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-2">
          <div className="relative sm:col-span-2">
            <input data-testid="coin-request-recipient" value={form.recipient} placeholder="Who? Name, email or BLC-XXXXXX"
              onChange={(e) => onRecipientChange(e.target.value)} autoComplete="off"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
            {sugs.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-pb-surface border border-pb-cyan/50 max-h-48 overflow-y-auto shadow-xl">
                {sugs.map((s) => (
                  <button key={s.id} type="button" data-testid={`request-sug-${s.id}`}
                    onClick={() => { setForm({ ...form, recipient: s.code || s.email }); setSugs([]); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-pb-elevated transition-colors border-b border-pb-border/40 last:border-0">
                    <Avatar src={s.avatar} name={s.name} size={26} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-white truncate">{s.name}</span>
                      <span className="block text-[10px] text-zinc-500 font-mono truncate">{s.code || s.email}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input data-testid="coin-request-amount" type="number" min={0} value={form.coins} placeholder="Coins"
            onChange={(e) => setForm({ ...form, coins: e.target.value })}
            className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan font-mono" />
        </div>
        <div className="flex gap-2">
          <input data-testid="coin-request-note" value={form.note} placeholder="What for? (optional)"
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
          <button data-testid="coin-request-send" onClick={request} disabled={busy || !form.recipient.trim() || !Number(form.coins)}
            className="border border-pb-cyan text-pb-cyan hover:bg-pb-cyan/10 font-black uppercase tracking-widest px-6 py-2.5 text-xs active:scale-95 transition-all disabled:opacity-40">
            {busy ? "…" : "Request"}
          </button>
        </div>
        {reqs.outgoing.length > 0 && (
          <div className="space-y-1.5" data-testid="coin-requests-outgoing">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your requests</div>
            {reqs.outgoing.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs px-3 py-2 bg-pb-elevated/40 border border-pb-border/50">
                <span className="text-zinc-300 truncate">{r.coins} BLC from {r.to_name}{r.note ? ` · ${r.note}` : ""}</span>
                <span className="font-bold uppercase tracking-widest text-[9px]" style={{ color: STATUS_COLORS[r.status] || "#71717A" }}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
